import { Component, h, State, Element, EventEmitter, Event, Prop, Method } from '@stencil/core';
import { getClassPrefix, star, Validator } from '../../utils/utils';
import classNames from 'classnames';
import { JackeryRating as _JackeryRating } from '../jackery-rating/jackery-rating';
import { JackeryDropdown as _JackeryDropdown } from '../jackery-dropdown/jackery-dropdown';
import { getLocaleComponentStrings, variableString } from "../../utils/locale";
import { JackeryUpload as _JackeryUpload } from '../jackery-upload/jackery-upload';
import {CommentOverview} from "../../common/interface"
import { JackeryButton as _JackeryButton } from '../jackery-button/jackery-button';


@Component({
  tag: 'jackery-comment-overview',
  styleUrl: 'jackery-comment-overview.css',
  shadow: true,
})

export class JackeryCommentOverview {
  @Element() element: HTMLElement;
  strings: any;
  @Prop() commentOverview: CommentOverview = {
    rating: "3.0",
    count: 0,
    list: [
      { rating: "5", num: 0},
      { rating: "4", num: 0},
      { rating: "3", num: 0},
      { rating: "2", num: 0},
      { rating: "1", num: 0},
    ]
  };
  @Prop() imgLimit: number; // 评论图片限制数量
  @Prop() isSubmitting: boolean = false;
  @State() active_sort: number | null;
  @State() write_review = false;
  @State() dropRef?: any;
  @State() activeFilterRating: string | null | undefined;
  @State() review_params: {
    name: string;
    email?: string;
    rating: number;
    image?: any;
    comment: string;
  } = {
    name: '',
    rating: 0,
    comment: ''
  }
  @State() review_error: {
    name: string;
    email: string;
    rating: string;
    image: string;
    comment: string;
  } = {
    name: '',
    email: '',
    rating: '',
    image: '',
    comment: ''
  }
  
  sortList: {key: string, value: number}[]
  validator__: any
  formRatingRef?: any;
  formImgUploadRef?: any;

  /**
   * 自定义事件 - 子组件状态变更传递到父组件
   */
  @Event() customEvent: EventEmitter<{
    name: string;
    email?: string;
    rating: number;
    image?: any;
    comment: string;
  }>;
  @Event() ratingFilterEvent: EventEmitter<string | null>;
  @Event() sortFilterEvent: EventEmitter<string | null>;

  @Method()
  public async handleCloseForm() { // 关闭评论表单(父组件调用执行)
    this.write_review = false;
    this.review_params = {
      name: '',
      rating: 0,
      comment: ''
    }
    this.formRatingRef?.handleReset();
    this.formImgUploadRef?.handleReset();
  }

  async componentWillLoad(): Promise<void> {
    this.strings = await getLocaleComponentStrings(this.element);
    this.sortList = [
      {key: this.strings.sort_most_recent, value: 1},
      {key: this.strings.sort_highest_rating, value: 2},
      {key: this.strings.sort_lowest_rating, value: 3},
      {key: this.strings.sort_with_Photos, value: 4}
    ]

    this.validator__ = new Validator({ // 表单校验器
      multis: true
    });
    this.initFormValidate()
  }

  private async handleSortChange(sort: number | null): Promise<any> {
    this.active_sort = sort
    this.sortFilterEvent.emit((sort || '').toString())
    await this.dropRef?.dropCallback(false);
  }

  handleRate(v: number) { // 确保每次修改属性时都会创建一个新的对象实例 - 动态更新rate
    this.review_params = {...this.review_params, rating: v}
    if(v !== 0 ) {
      this.review_error.rating = ''
    }
  }

  handleFileUploaded(event: CustomEvent<File[]>) {
    const selectedFile = event.detail;
    this.review_params = {...this.review_params, image: selectedFile}
  }

  handleName(v: string) {
    this.review_params = {...this.review_params, name: v}

    // 动态校验
    const res = this.validator__.validateItem('name', v)
    if(res) {
      this.review_error = {...this.review_error, [res.key]: res.msg}
      return
    }
    this.review_error = {...this.review_error, name: ""}
  }

  handleEmail(v: string) {
    this.review_params = {...this.review_params, email: v}
  }

  handleComment(v: string) {
    this.review_params = {...this.review_params, comment: v}
    // 动态校验
    const res = this.validator__.validateItem('comment', v)
    if(res) {
      this.review_error = {...this.review_error, [res.key]: res.msg}
      return
    }
    this.review_error = {...this.review_error, comment: ""}
  }

  initFormValidate(): void {
    this.validator__.resetValue()
    this.validator__.add(this.review_params.name, [{
          strategy: 'isNonEmpty',
          errorMsg: this.strings.form_name_error
      }
    ], "name")

    this.validator__.add(this.review_params.email, [
      {
        strategy: 'isEmail',
        errorMsg: this.strings.form_email_error
      }
    ], "email")

    this.validator__.add(this.review_params.rating, [
      {
        strategy: 'isNonEmpty',
        errorMsg: this.strings.form_rating_error
      }
    ], "rate")

    this.validator__.add(this.review_params.comment, [
      {
        strategy: 'isNonEmpty',
        errorMsg: this.strings.form_comment_error
      }
    ], "comment")
  }

  handleSubmit(v) {
    this.initFormValidate()
    const errorMsg = this.validator__.validate();
    this.review_error = { name: '', email: '', rating: '', image: '', comment: '' }
    errorMsg.forEach(msg => {
      this.review_error = {...this.review_error, [msg.key]: msg.msg}
    });

    if(this.review_params.rating === 0) {
      this.review_error.rating = this.strings.form_rating_error;
      return
    }
    
    if(errorMsg && (errorMsg as Array<any>).length < 1) {
      this.customEvent.emit(this.review_params);
    }
    v.preventDefault()
  }

  handleFilterRating(v) {
    this.activeFilterRating = this.activeFilterRating === v ? null : v
    this.ratingFilterEvent.emit(this.activeFilterRating);
  }

  render() {
    const classPrefix: string = getClassPrefix('comment-overview');
    const classes: string = classNames(classPrefix);
    return (
      <div class={classes}>
        <div class={`${classPrefix}_overview_inner`}>
          <div class={`${classPrefix}_overview_title`}>{this.strings.title}</div>
          <div class={`${classPrefix}_overview_line`} />
          <div class={`${classPrefix}_rating_container`}>
            <div class={`${classPrefix}_rating_inner`}>
              <div class={`${classPrefix}_rating_item`}>
                <div class={`${classPrefix}_average_rating_container`}>
                  <div class={`${classPrefix}_average_rating_box`}>
                    <span class={`${classPrefix}_average_rating`}>{this.commentOverview.rating}</span>
                    <span class={`${classPrefix}_total_rating`}>/5</span>
                  </div>
                  <jackery-rating score={parseInt(this.commentOverview.rating)}></jackery-rating>
                  <p class={`${classPrefix}_total_reviews`}>{variableString(this.strings.total_reviews_abstract, this.commentOverview.count.toString())}</p>
                </div>
              </div>

              <div class={`${classPrefix}_rating_item review_rate_section flex flex-col`}>
              {this.commentOverview.list.map((item) =>
                <div 
                  onClick={() => this.handleFilterRating(item.rating)} 
                  class={{
                    'areviews_rating_filter flex items-center': true,
                    'active': this.activeFilterRating === item.rating.toString() 
                  }}
                  data-rating={item.rating}>
                  <div class="flex items-center">
                    {item.rating}
                    <div class="icon-star" innerHTML={star()}></div>
                  </div>

                  <div 
                    class="progress_bar_con"
                    style={{
                      '--percent': `${item.percent ? item.percent : 0}`
                    }}
                  >
                    <div class="progress_bar_inner"></div>
                  </div>

                  <div class="total_reviews_number_Rating">
                    <span>{item.num}</span>
                  </div>
                </div>
              )}
              </div>

              <div class={`${classPrefix}_rating_item`}>
                <jackery-button 
                  shape="square" 
                  type="primary"
                  onClick={() => {this.write_review = !this.write_review}}
                >{this.strings.write_review_btn}</jackery-button>
              </div>
            </div>
          </div>

          <div class={{
            'enter_reviews': true,
            'active': this.write_review
          }}>
           <div class="enter_reviews_form">
            <form id="enter_reviews">
              <div class={
                {
                  "form-line-item": true,
                  "flex": true,
                  "error": this.review_error.name !== ''
                }
              }>
                <label htmlFor="comment_name">{this.strings.form_name_label}</label>
                <input
                  type="text"
                  name="comment_name"
                  id="comment_name"
                  placeholder={this.strings.form_name_placeholder}
                  required
                  maxlength="200"
                  value={this.review_params.name}
                  onInput={v => this.handleName((v.target as HTMLInputElement).value)}
                />
                {this.review_error.name && <div class="line-item-error">{this.review_error.name}</div>}
              </div>

              <div class={
                {
                  "form-line-item": true,
                  "flex": true,
                  "error": this.review_error.email !== ''
                }
              }>
                <label htmlFor="comment_email">Email (Optional)</label>
                <input
                  type="text"
                  name="comment_email"
                  id="comment_email"
                  placeholder="Your Email"
                  required
                  maxlength="200"
                  value={this.review_params.email}
                  onInput={v => this.handleEmail((v.target as HTMLInputElement).value)}
                />
                {this.review_error.email && <div class="line-item-error">{this.review_error.email}</div>}
              </div>

              <div class="form-line-item flex form-line-item__star">
                <label htmlFor="comment_email">Your rating</label>
                <jackery-rating
                  mode="mode__edit"
                  ref={el => (this.formRatingRef = el)}
                  onRatingChange={(v) => {this.handleRate(v.detail)}}
                />
                <div class="line-item__score">{this.review_params.rating}<span>/</span>5</div>
              </div>
              {this.review_error.rating && <div class="line-item-error line-item-error__rating">{this.review_error.rating}</div>}

              <div class="form-line-item flex form-line-item__upload">
                <jackery-upload 
                  ref={el => (this.formImgUploadRef = el)}
                  limit={this.imgLimit}
                  onFileUploaded={(event) => this.handleFileUploaded(event)}
                  accept="image/png, image/jpeg"
                />
              </div>

              <div class={
                {
                  "form-line-item": true,
                  "flex": true,
                  "form-line-item__area": true,
                  "error": this.review_error.comment !== ''
                }
              }>
                <textarea
                  cols={20} 
                  name="review_comment"
                  id="review_comment"
                  rows={5}
                  placeholder="Enter Your Comment"
                  maxlength="5000"
                  required
                  value={this.review_params.comment}
                  onInput={v => this.handleComment((v.target as HTMLTextAreaElement).value)}
                />
                {this.review_error.comment && <div class="line-item-error">{this.review_error.comment}</div>}
              </div>

              <jackery-button 
                  shape="square" 
                  type="primary"
                  loading={this.isSubmitting ? 'loading' : undefined}
                  onClick={(e) => this.handleSubmit(e)}
                >Send Review</jackery-button>
            </form>
           </div>
          </div>

          <jackery-dropdown is-right ref={el => (this.dropRef = el)} >
            <div class="sort-box flex items-center" slot="trigger">
              <button class="flex items-center">
                <span>{this.strings.sort}</span>
                <svg xmlns="http://www.w3.org/2000/svg" width="10" height="10" viewBox="0 0 10 10">
                  <path d="M0 0 L5 7 L10 0" fill="none" stroke="black" stroke-width="1" />
                </svg>
              </button>
            </div>

            {this.sortList.map((item) =>
              <div 
                class={{
                  "dropdown-item": true,
                  "is-active": this.active_sort === item.value
                }} 
                data-value={item.value}
                onClick={() => this.handleSortChange(item.value)}
              >{item.key}</div>
            )}
          </jackery-dropdown>
          <div class={`${classPrefix}_overview_line`} />
        </div>
      </div>
    );
  }
}
